<template>
  <div class="container mx-auto px-4 py-8">
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900 mb-2">Importar Usuarios</h1>
        <p class="text-gray-600">Importa usuarios desde un archivo CSV</p>
      </div>

      <!-- Role Selection -->
      <div class="mb-6">
        <label for="role" class="block text-sm font-medium text-gray-700 mb-2">
          Rol para todos los usuarios
        </label>
        <select
          id="role"
          v-model="selectedRole"
          class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          required
        >
          <option value="">Selecciona un rol</option>
          <option value="validador">Validador</option>
          <option value="digitador">Digitador</option>
          <option value="supervisor">Supervisor</option>
          <option value="transmisor">Transmisor</option>
          <option value="auditor">Auditor</option>
          <option value="admin">Administrador</option>
        </select>
      </div>

      <!-- Username Options -->
      <div class="mb-6">
        <div class="flex items-center">
          <input
            id="use-phone-as-username"
            v-model="usePhoneAsUsername"
            type="checkbox"
            class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          >
          <label for="use-phone-as-username" class="ml-2 block text-sm text-gray-900">
            Usar número de teléfono como nombre de usuario
          </label>
        </div>
        <p class="mt-1 text-sm text-gray-500">
          Si se activa, se usará el teléfono como username (se limpiarán espacios y guiones automáticamente)
        </p>
      </div>

      <!-- Group By Identifier Option (solo para transmisor/auditor) -->
      <div v-if="selectedRole === 'transmisor' || selectedRole === 'auditor'" class="mb-6">
        <div class="flex items-center">
          <input
            id="group-by-identifier"
            v-model="groupByIdentifier"
            type="checkbox"
            class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          >
          <label for="group-by-identifier" class="ml-2 block text-sm text-gray-900">
            Agrupar establecimientos por Username/Teléfono
          </label>
        </div>
        <p class="mt-1 text-sm text-gray-500">
          Activa esta opción si el archivo viene con una fila por establecimiento. El sistema agrupará automáticamente todas las filas que tengan el mismo username (o teléfono si usas "Usar teléfono como username"), asignando todos los establecimientos encontrados al mismo usuario.
        </p>
        <div class="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
          <p class="text-xs text-blue-800">
            <strong>Ejemplo:</strong> Si tienes 3 filas con username "jperez" y establecimientos "1", "2", "3" respectivamente, se creará 1 solo usuario con los 3 establecimientos asignados. <strong>Nota:</strong> El DNI se usa solo para la contraseña temporal.
          </p>
        </div>
      </div>

      <!-- Upload Section -->
      <div class="bg-white shadow rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4">Subir Archivo CSV</h2>
        
        <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <div class="mx-auto h-12 w-12 text-gray-400 mb-4">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
            </svg>
          </div>
          
          <div class="text-sm text-gray-600">
            <label for="file-upload" class="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
              <span>Selecciona un archivo</span>
              <input
                id="file-upload"
                name="file-upload"
                type="file"
                class="sr-only"
                accept=".csv"
                @change="handleFileSelect"
              >
            </label>
            <p class="pl-1">o arrastra y suelta aquí</p>
          </div>
          <p class="text-xs text-gray-500 mt-2">Solo archivos CSV hasta 50MB</p>
        </div>

        <div v-if="selectedFile" class="mt-4 p-4 bg-gray-50 rounded-lg">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div class="h-8 w-8 text-green-500 mr-3">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-900">{{ selectedFile.name }}</p>
                <p class="text-sm text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
              </div>
            </div>
            <button
              @click="removeFile"
              class="text-red-500 hover:text-red-700"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Format Instructions -->
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h3 class="text-lg font-medium text-blue-900 mb-3">Formato del Archivo CSV</h3>
        <p class="text-blue-800 mb-3">El archivo debe contener las siguientes columnas obligatorias:</p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <ul class="space-y-1 text-blue-700">
              <li>• <strong>firstName</strong> (nombre)</li>
              <li>• <strong>lastName</strong> (apellido)</li>
              <li>• <strong>email</strong> (correo electrónico)</li>
              <li>• <strong>username</strong> (nombre de usuario)</li>
            </ul>
          </div>
          <div>
            <ul class="space-y-1 text-blue-700">
              <li>• <strong>dni</strong> (documento)</li>
              <li>• <strong>telefono</strong> (teléfono)</li>
              <li v-if="selectedRole === 'transmisor' || selectedRole === 'auditor'">• <strong>establecimientos</strong> (IDs separados por | o ,)</li>
            </ul>
          </div>
        </div>
        <div class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p class="text-sm text-yellow-800">
            <strong>Nota:</strong> La contraseña inicial será el DNI del usuario. 
            <span v-if="selectedRole === 'transmisor' || selectedRole === 'auditor'">Para usuarios transmisores y auditores, la columna <strong>establecimientos</strong> es obligatoria (formato: "40|41|43" o "40,41,43").</span>
          </p>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-between">
        <router-link
          to="/users"
          class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancelar
        </router-link>
        
        <button
          @click="importUsers"
          :disabled="!selectedFile || !selectedRole || importing"
          class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="importing" class="flex items-center">
            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Importando...
          </span>
          <span v-else>Importar Usuarios</span>
        </button>
      </div>

      <!-- Results Modal -->
      <div v-if="showResults" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="closeResults">
        <div class="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white" @click.stop>
          <div class="mt-3">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-medium text-gray-900">Resultados de Importación</h3>
              <button @click="closeResults" class="text-gray-400 hover:text-gray-600">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            <!-- Summary -->
            <div class="grid grid-cols-3 gap-4 mb-6">
              <div class="bg-blue-50 p-4 rounded-lg text-center">
                <div class="text-2xl font-bold text-blue-600">{{ importResults?.summary?.totalProcessed || 0 }}</div>
                <div class="text-sm text-blue-800">Procesados</div>
              </div>
              <div class="bg-green-50 p-4 rounded-lg text-center">
                <div class="text-2xl font-bold text-green-600">{{ importResults?.summary?.successful || 0 }}</div>
                <div class="text-sm text-green-800">Exitosos</div>
              </div>
              <div class="bg-red-50 p-4 rounded-lg text-center">
                <div class="text-2xl font-bold text-red-600">{{ importResults?.summary?.errors || 0 }}</div>
                <div class="text-sm text-red-800">Errores</div>
              </div>
            </div>

            <!-- Errors List -->
            <div v-if="importResults?.errors?.length > 0" class="mb-4">
              <h4 class="text-md font-medium text-red-900 mb-2">Errores encontrados:</h4>
              <div class="max-h-60 overflow-y-auto">
                <div v-for="error in importResults.errors" :key="error.row" class="bg-red-50 border border-red-200 rounded p-3 mb-2">
                  <div class="text-sm">
                    <span class="font-medium text-red-800">Fila {{ error.row }}:</span>
                    <span class="text-red-700 ml-2">{{ error.error || error.message }}</span>
                  </div>
                  <div v-if="error.data" class="text-xs text-gray-600 mt-1">
                    Usuario: {{ error.data.firstName }} {{ error.data.lastName }} ({{ error.data.username }})
                  </div>
                </div>
              </div>
            </div>

            <!-- Success List -->
            <div v-if="importResults?.results?.length > 0" class="mb-4">
              <h4 class="text-md font-medium text-green-900 mb-2">Usuarios creados exitosamente:</h4>
              <div class="max-h-60 overflow-y-auto">
                <div v-for="result in importResults.results.filter(r => r.status === 'success')" :key="result.row" class="bg-green-50 border border-green-200 rounded p-3 mb-2">
                  <div class="text-sm">
                    <span class="font-medium text-green-800">{{ result.username }}</span>
                    <span class="text-green-700 ml-2">({{ result.email }})</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex justify-end">
              <button
                @click="closeResults"
                class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { apiService } from '../services/apiService'

const router = useRouter()
const toast = useToast()

const selectedFile = ref<File | null>(null)
const selectedRole = ref('')
const usePhoneAsUsername = ref(false)
const groupByIdentifier = ref(false)
const importing = ref(false)
const showResults = ref(false)
const importResults = ref<any>(null)

function handleFileSelect(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    selectedFile.value = target.files[0]
  }
}

function removeFile() {
  selectedFile.value = null
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

async function importUsers() {
  if (!selectedFile.value) {
    toast.error('Por favor selecciona un archivo')
    return
  }

  if (!selectedRole.value) {
    toast.error('Por favor selecciona un rol')
    return
  }

  try {
    importing.value = true
    
    const formData = new FormData()
    formData.append('csvFile', selectedFile.value)
    formData.append('role', selectedRole.value)
    formData.append('usePhoneAsUsername', usePhoneAsUsername.value.toString())
    formData.append('groupByIdentifier', groupByIdentifier.value.toString())
    
    const response = await apiService.importUsersFromCSV(formData)
    importResults.value = response
    
    if ((response as any).summary.successful > 0) {
      toast.success(`${(response as any).summary.successful} usuarios importados exitosamente`)
    }
    
    if ((response as any).summary.errors > 0) {
      toast.warning(`${(response as any).summary.errors} errores encontrados. Revisa los detalles.`)
    }
    
    showResults.value = true
    
  } catch (error: any) {
    toast.error('Error importando usuarios: ' + (error.response?.data?.message || error.message))
  } finally {
    importing.value = false
  }
}

function closeResults() {
  showResults.value = false
  importResults.value = null
  // Optionally refresh the users list or redirect
  router.push('/users')
}
</script>
